import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { History, Loader2, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errMsg } from "../../lib/api";
import { ddmmyyyy, inr, num } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { ConfirmDialog } from "./ConfirmDialog";

const UOMS = ["Mtr", "Pcs", "Kg"];
const GSTS = [0, 5, 12, 18];

const ItemDialog = ({ item, onClose, onSaved }) => {
  const [f, setF] = useState({ code: item.code, description: item.description, uom: item.uom, rate: item.rate ?? 0, gst: item.gst ?? 5 });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/item-master/${item.id}`, { ...f, rate: Number(f.rate) || 0, gst: Number(f.gst) });
      toast.success("Item updated");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="item-dialog">
        <DialogHeader>
          <DialogTitle>Edit item</DialogTitle>
          <DialogDescription>{item.vendor_name} · {item.product_line}. Changes apply to new POs; saved POs and existing stock keep their old name and code.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div><label className="label">Code</label><input className="field mono" required value={f.code} onChange={set("code")} data-testid="item-code-input" /></div>
            <div className="col-span-2"><label className="label">Item name</label><input className="field" required value={f.description} onChange={set("description")} data-testid="item-description-input" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="label">UOM</label>
              <select className="field" value={f.uom} onChange={set("uom")} data-testid="item-uom-select">{UOMS.map((u) => <option key={u}>{u}</option>)}</select></div>
            <div><label className="label">Last rate ₹</label><input className="field mono" type="number" min="0" step="any" value={f.rate} onChange={set("rate")} data-testid="item-rate-input" /></div>
            <div><label className="label">GST %</label>
              <select className="field" value={f.gst} onChange={set("gst")} data-testid="item-gst-select">{GSTS.map((g) => <option key={g} value={g}>{g}</option>)}</select></div>
          </div>
          <button className="btn btn-primary w-full" disabled={busy} data-testid="item-save-btn">{busy && <Loader2 size={18} className="animate-spin" />} Save item</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const HistoryDialog = ({ item, onClose }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/item-master/${item.id}/history`).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [item.id]);
  const rates = (rows || []).filter((r) => r.status !== "Cancelled").map((r) => r.rate);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto" data-testid="item-history-dialog">
        <DialogHeader>
          <DialogTitle><span className="mono text-brand">{item.code}</span> {item.description}</DialogTitle>
          <DialogDescription>Rates paid to {item.vendor_name}, newest first.</DialogDescription>
        </DialogHeader>
        {!rows ? <p className="text-muted-foreground">Loading…</p> : !rows.length ? <p className="text-sm text-muted-foreground">Not on any PO yet.</p> : (
          <>
            {rates.length > 1 && <p className="text-sm" data-testid="item-history-range">Lowest <b className="mono">{inr(Math.min(...rates))}</b> · Highest <b className="mono">{inr(Math.max(...rates))}</b></p>}
            <div className="divide-y">
              {rows.map((r, i) => {
                const prev = rows.slice(i + 1).find((x) => x.status !== "Cancelled");
                const pct = prev && prev.rate ? Math.round(((r.rate - prev.rate) / prev.rate) * 1000) / 10 : null;
                return (
                  <div key={`${r.po_id}-${i}`} className="flex items-center justify-between gap-2 py-2 text-sm" data-testid={`item-history-row-${i}`}>
                    <div className="min-w-0">
                      <p className="mono font-semibold">{r.po_no} <span className="font-sans text-xs font-normal text-muted-foreground">{ddmmyyyy(r.po_date)} · {r.status}</span></p>
                      <p className="text-xs text-muted-foreground">{num(r.qty)} {r.uom}{r.colour ? ` · ${r.colour}` : ""}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className={`mono font-bold ${r.status === "Cancelled" ? "text-muted-foreground line-through" : ""}`}>{inr(r.rate)}</p>
                      {pct != null && pct !== 0 && r.status !== "Cancelled" && <p className={`text-xs font-semibold ${pct > 0 ? "text-destructive" : "text-brand"}`}>{pct > 0 ? "▲ +" : "▼ "}{pct}%</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ItemRow = ({ it, canEdit, isAdmin, onEdit, onDelete, onHistory }) => (
  <div className="flex items-start gap-3 border-t py-3 first:border-t-0" data-testid={`item-row-${it.code}`}>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold"><span className="mono mr-2 text-brand">{it.code}</span>{it.description}</p>
      <p className="text-xs text-muted-foreground">{it.product_line} · {it.uom} · last {inr(it.rate || 0)} · {it.gst}% GST · on {it.po_count} PO{it.po_count === 1 ? "" : "s"}</p>
    </div>
    <button className="icon-btn" onClick={() => onHistory(it)} aria-label="Price history" data-testid={`item-history-${it.code}`}><History size={18} /></button>
    {canEdit && <button className="icon-btn" onClick={() => onEdit(it)} aria-label="Edit item" data-testid={`item-edit-${it.code}`}><Pencil size={18} /></button>}
    {isAdmin && <button className="icon-btn hover:!text-destructive" onClick={() => onDelete(it)} aria-label="Delete item" data-testid={`item-delete-${it.code}`}><Trash2 size={18} /></button>}
  </div>
);

export const ItemsTab = () => {
  const { isAdmin, can } = useAuth();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [history, setHistory] = useState(null);
  const load = () => api.get("/item-master").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);
  const groups = useMemo(() => {
    const s = q.trim().toLowerCase();
    const g = {};
    (items || []).filter((i) => !s || `${i.code} ${i.description} ${i.vendor_name} ${i.vendor_code}`.toLowerCase().includes(s))
      .forEach((i) => { (g[`${i.vendor_name}|${i.vendor_code}`] ||= []).push(i); });
    return Object.entries(g).sort();
  }, [items, q]);
  const remove = async () => {
    try {
      await api.delete(`/item-master/${deleting.id}`);
      toast.success("Item removed from the list");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setDeleting(null);
  };
  if (!items) return <p className="text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-3" data-testid="items-tab">
      <input className="field" placeholder="Search code, item or supplier" value={q} onChange={(e) => setQ(e.target.value)} data-testid="items-search-input" />
      <p className="px-1 text-xs text-muted-foreground">Codes are created automatically when a PO is saved. {items.length} item{items.length === 1 ? "" : "s"}.</p>
      {!groups.length && <p className="panel p-6 text-center text-muted-foreground" data-testid="items-empty">{items.length ? "No match." : "No items yet. They appear here after you save a PO."}</p>}
      {groups.map(([k, list]) => {
        const [name, code] = k.split("|");
        return (
          <div key={k} className="panel p-4" data-testid={`items-vendor-${code}`}>
            <p className="font-heading font-bold">{name} {code && <span className="mono text-xs text-muted-foreground">({code})</span>}</p>
            {list.map((it) => <ItemRow key={it.id} it={it} canEdit={can("vendor_write")} isAdmin={isAdmin} onEdit={setEditing} onDelete={setDeleting} onHistory={setHistory} />)}
          </div>
        );
      })}
      {history && <HistoryDialog item={history} onClose={() => setHistory(null)} />}
      {editing && <ItemDialog item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <ConfirmDialog open={!!deleting} onCancel={() => setDeleting(null)} onConfirm={remove}
        title={`Remove ${deleting?.code} from the list?`} body="Saved POs and stock are not changed. If this item is ordered again it gets a new code." />
    </div>
  );
};
